import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { registrationsAPI } from '../../services/api';
import { Calendar, Clock, MapPin, AlertCircle, CheckCircle, XCircle, Clock as ClockIcon, PartyPopper, TrendingUp, ArrowRight } from 'lucide-react';

interface Registration {
  id: number;
  order_number: string;
  child: number;
  child_name: string;
  session_name: string;
  program_name: string;
  status: string;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  location: string;
  days_of_week: string;
  amount_paid: string;
  discount_applied: string;
  progress?: {
    lessons_completed: number;
    total_lessons: number;
    progress_percent: number;
    is_program_completed: boolean;
  };
  latest_evaluation?: {
    id: number;
    eligible: boolean;
    notes: string;
    next_program_id: number | null;
    next_program_name: string | null;
  } | null;
  next_program_info?: {
    id: number;
    name: string;
    level_number: number | null;
    monthly_price: string;
  } | null;
}

const MyRegistrations: React.FC = () => {
  const { data: registrations, isLoading: loadingRegistrations } = useQuery('myRegistrations', async () => {
    const response = await registrationsAPI.getRegistrations();
    return response.data.results || response.data;
  });

  const getStatusBadge = (status: string) => {
    const badges: { [key: string]: { color: string; icon: any } } = {
      confirmed: { color: 'bg-green-100 text-green-800', icon: <CheckCircle size={16} /> },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: <ClockIcon size={16} /> },
      cancelled: { color: 'bg-red-100 text-red-800', icon: <XCircle size={16} /> },
      waitlisted: { color: 'bg-blue-100 text-blue-800', icon: <AlertCircle size={16} /> },
    };
    
    const badge = badges[status] || badges.pending;
    
    return (
      <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
        {badge.icon}
        <span className="ml-1">{status.charAt(0).toUpperCase() + status.slice(1)}</span>
      </span>
    );
  };

  if (loadingRegistrations) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">My Registrations</h1>
        <p className="text-gray-600 mt-2">View your enrolled sessions and manage attendance</p>
      </div>

      {/* Active Registrations */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Active Enrollments</h2>
        {registrations && registrations.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2">
            {registrations.map((registration: Registration) => (
              <div key={registration.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{registration.program_name}</h3>
                      <p className="text-sm text-gray-600">{registration.session_name}</p>
                      <p className="text-xs text-gray-500 mt-1">Order #{registration.order_number}</p>
                    </div>
                    {getStatusBadge(registration.status)}
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <AlertCircle size={16} className="mr-2 text-gray-400" />
                      <span><strong>Child:</strong> {registration.child_name}</span>
                    </div>
                    {(registration.start_date || registration.end_date) && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar size={16} className="mr-2 text-gray-400" />
                        <span>{registration.start_date || 'TBD'} to {registration.end_date || 'TBD'}</span>
                      </div>
                    )}
                    {registration.days_of_week && registration.days_of_week !== 'N/A' && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock size={16} className="mr-2 text-gray-400" />
                        <span>{registration.days_of_week}{registration.start_time ? ` • ${registration.start_time} - ${registration.end_time}` : ''}</span>
                      </div>
                    )}
                    {registration.location && registration.location !== 'null' && (
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin size={16} className="mr-2 text-gray-400" />
                        <span>{registration.location}</span>
                      </div>
                    )}
                    {registration.amount_paid && (
                      <div className="flex items-center text-sm text-gray-600">
                        <span className="font-medium">Amount Paid: ${registration.amount_paid}</span>
                        {registration.discount_applied && parseFloat(registration.discount_applied) > 0 && (
                          <span className="ml-2 text-green-600 font-medium">
                            (Discount: -${parseFloat(registration.discount_applied).toFixed(2)})
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Lesson Progress */}
                  {registration.progress && registration.progress.total_lessons > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                        <span>Lesson Progress</span>
                        <span>{registration.progress.lessons_completed}/{registration.progress.total_lessons}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                          className={`h-2.5 rounded-full ${registration.progress.is_program_completed ? 'bg-green-500' : 'bg-primary-500'}`}
                          style={{ width: `${registration.progress.progress_percent}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Congratulations banner on completion */}
                  {registration.progress?.is_program_completed && (
                    <div className="mb-4 rounded-lg bg-green-50 border border-green-200 p-3 flex items-start gap-2">
                      <PartyPopper className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                      <p className="text-sm text-green-800">
                        <span className="font-semibold">Congratulations!</span> {registration.child_name} finished this level.
                        {' '}Check our programs to see the next level.
                      </p>
                    </div>
                  )}

                  {/* Evaluation: eligible for next level */}
                  {registration.latest_evaluation && (
                    <div className="mb-4 rounded-lg bg-indigo-50 border border-indigo-200 p-4">
                      <div className="flex items-start gap-2 mb-2">
                        <TrendingUp className="text-indigo-600 flex-shrink-0 mt-0.5" size={18} />
                        <p className="text-sm text-indigo-900">
                          <span className="font-semibold">{registration.child_name} is ready for {registration.latest_evaluation.next_program_name || 'the next level'}!</span>
                          {registration.latest_evaluation.notes && (
                            <span className="block text-indigo-700 mt-1">{registration.latest_evaluation.notes}</span>
                          )}
                        </p>
                      </div>
                      {registration.latest_evaluation.next_program_id && (
                        <Link
                          to={`/registration?program=${registration.latest_evaluation.next_program_id}&child=${registration.child}`}
                          className="inline-flex items-center gap-1 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-3 py-1.5 mt-1"
                        >
                          Enroll in {registration.latest_evaluation.next_program_name} <ArrowRight size={14} />
                        </Link>
                      )}
                    </div>
                  )}

                  {/* Standing invitation to move up, even without a formal evaluation yet */}
                  {!registration.latest_evaluation && registration.next_program_info && registration.progress?.is_program_completed && (
                    <Link
                      to={`/registration?program=${registration.next_program_info.id}&child=${registration.child}`}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-primary-700 hover:text-primary-800"
                    >
                      Enroll in {registration.next_program_info.name} <ArrowRight size={14} />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Active Registrations</h3>
            <p className="text-gray-600">You don't have any active enrollments yet.</p>
          </div>
        )}
      </div>

    </div>
  );
};

export default MyRegistrations;
